import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Calendar, Clock, FileText, Linkedin, Mail, Share2 } from 'lucide-react';
import { articleContent } from '../data/articles-content';
import { profile } from '../data/portfolio';

// Calculate word count from markdown text
const calculateWordCount = (text) => {
  if (!text) return 0;
  // Remove markdown syntax and split by whitespace
  const cleanText = text
    .replace(/#+\s/g, '') // Remove markdown headings
    .replace(/\*\*|__|\*|_|`/g, '') // Remove bold, italic, code markers
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Extract link text
    .replace(/[!]?\[([^\]]+)\]\([^\)]+\)/g, '$1') // Extract image alt text
    .replace(/^[-*+]\s/gm, '') // Remove bullet points
    .replace(/^\d+\.\s/gm, '') // Remove numbered lists
    .replace(/^>\s/gm, '') // Remove blockquotes
    .replace(/^```[\s\S]*?```/gm, '') // Remove code blocks
    .trim();
  
  // Split by whitespace and filter empty strings
  const words = cleanText.split(/\s+/).filter(word => word.length > 0);
  return words.length;
};

// Convert text to URL-friendly slug
const textToSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '-'); // Replace spaces with hyphens
};

// Extract headings from markdown text
const extractHeadings = (markdown) => {
  const headings = [];
  const lines = markdown.split('\n');
  
  lines.forEach((line) => {
    const h2Match = line.match(/^## (.+)$/);
    const h3Match = line.match(/^### (.+)$/);
    
    if (h2Match) {
      headings.push({
        level: 2,
        text: h2Match[1].trim(),
        id: textToSlug(h2Match[1].trim()),
      });
    } else if (h3Match) {
      headings.push({
        level: 3,
        text: h3Match[1].trim(),
        id: textToSlug(h3Match[1].trim()),
      });
    }
  });
  
  return headings;
};

// Table of Contents component
const TableOfContents = ({ headings, activeId }) => {
  const handleHeadingClick = (e, headingId) => {
    e.preventDefault();
    const element = document.getElementById(headingId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="hidden lg:block sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
      <nav className="pl-6 border-l border-border/60">
        <div className="mono uppercase tracking-widest text-[10.5px] text-ink-mute mb-4 font-semibold">
          On this page
        </div>
        <ul className="space-y-2.5">
          {headings.map((heading) => (
            <li key={heading.id}>
              <a
                href="#"
                onClick={(e) => handleHeadingClick(e, heading.id)}
                className={`block text-[13px] leading-relaxed transition-colors cursor-pointer ${
                  activeId === heading.id
                    ? 'text-brand font-medium'
                    : 'text-ink-soft hover:text-foreground'
                } ${heading.level === 3 ? 'ml-4 text-[12px]' : ''}`}
              >
                {heading.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

const loadMermaid = (() => {
  let promise;
  return () => {
    if (typeof window === 'undefined') return Promise.reject(new Error('window is undefined'));
    if (promise) return promise;

    promise = import('mermaid')
      .then((module) => module.default ?? module)
      .then((mermaid) => {
        if (!mermaid || typeof mermaid.initialize !== 'function') {
          throw new Error('Mermaid module did not initialize correctly');
        }
        mermaid.initialize({ startOnLoad: false, theme: 'default' });
        return mermaid;
      });

    return promise;
  };
})();

const MermaidBlock = ({ chart }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!containerRef.current) return;

    loadMermaid()
      .then((mermaid) => {
        if (cancelled) return;
        const id = `mermaid-${Math.random().toString(36).slice(2, 9)}`;
        return mermaid.render(id, chart);
      })
      .then((result) => {
        if (cancelled || !containerRef.current || !result?.svg) return;
        containerRef.current.innerHTML = result.svg;
      })
      .catch((error) => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = `<pre class="mermaid-error">${String(error.message || error)}</pre>`;
      });

    return () => {
      cancelled = true;
    };
  }, [chart]);

  return <div ref={containerRef} className="mermaid-chart" />;
};

const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  if (!inline && match?.[1] === 'mermaid') {
    const chart = String(children).replace(/\n$/, '');
    return <MermaidBlock chart={chart} />;
  }
  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

// Custom heading renderer that adds IDs for TOC navigation
const HeadingRenderer = ({ level, children }) => {
  const text = typeof children === 'string' ? children : String(children);
  const id = textToSlug(text);
  
  if (level === 2) {
    return (
      <h2 id={id} className="group">
        {children}
      </h2>
    );
  }
  if (level === 3) {
    return (
      <h3 id={id} className="group">
        {children}
      </h3>
    );
  }
  
  return React.createElement(`h${level}`, { id }, children);
};

const ImageRenderer = ({ src, alt, ...props }) => {
  const prefix = process.env.PUBLIC_URL || '';
  const resolved = src && src.startsWith('/') ? `${prefix}${src}` : src;
  return <img src={resolved} alt={alt} {...props} />;
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (e) {
    return iso;
  }
};

const Article = () => {
  const { slug } = useParams();
  const article = articleContent.find((a) => a.slug === slug);
  const [activeId, setActiveId] = useState('');
  const articleBodyRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  // Track active section for TOC highlight
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '0px 0px -70% 0px' }
    );

    if (articleBodyRef.current) {
      const headings = articleBodyRef.current.querySelectorAll('h2, h3');
      headings.forEach((heading) => observer.observe(heading));
    }

    return () => observer.disconnect();
  }, [article]);

  if (!article) return <Navigate to="/writing" replace />;

  const headings = extractHeadings(article.body);

  // Generate share URLs
  const articleUrl = `${window.location.origin}/#/writing/${slug}`;
  const shareText = `${article.title} by ${profile.name}`;
  
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(articleUrl)}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-12 max-w-6xl mx-auto px-6 pb-16">
      <article>
        <div className="pt-10 md:pt-14">
          <Link
            to="/writing"
            className="inline-flex items-center gap-2 text-[12.5px] mono uppercase tracking-widest text-ink-mute hover:text-brand transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Back to writing
          </Link>
        </div>

        <header className="mb-12 fade-up">
          <div className="flex flex-wrap items-center gap-3 mono uppercase tracking-widest text-[10.5px] text-ink-mute mb-6">
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={12} /> {formatDate(article.date)}
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} /> {article.readingTime} min read
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5">
              <FileText size={12} /> {calculateWordCount(article.body)} words
            </span>
            {article.tags?.length > 0 && (
              <>
                <span>·</span>
                <span>{article.tags.join(' · ')}</span>
              </>
            )}
          </div>

          <h1 className="text-[28px] md:text-[36px] leading-[1.1] tracking-tight font-medium">
            {article.title}
          </h1>
          {article.subtitle && (
            <p className="mt-4 text-[18px] md:text-[19px] text-ink-soft leading-relaxed serif-italic">
              {article.subtitle}
            </p>
          )}

          <div className="mt-8 pt-6 border-t border-border/60 flex items-center gap-3 text-[13px] text-ink-soft">
            <div className="w-9 h-9 rounded-full bg-brand/20 border border-brand/40 flex items-center justify-center text-brand text-[13px] font-semibold">
              VW
            </div>
            <div className="flex-1">
              <div className="text-foreground font-medium">{profile.name}</div>
              <div className="text-[12px] text-ink-mute">{profile.role} · {profile.discipline}</div>
            </div>
            <a
              href={profile.socials.linkedin}
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-ink-soft hover:text-brand hover:border-brand/50 transition-colors"
            >
              <Linkedin size={14} />
            </a>
            <a
              href={profile.socials.email}
              aria-label="Email"
              className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-ink-soft hover:text-brand hover:border-brand/50 transition-colors"
            >
              <Mail size={14} />
            </a>
          </div>
        </header>

        <div className="article-body fade-up max-w-2xl" ref={articleBodyRef}>
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]} 
            components={{ 
              code: CodeBlock,
              h2: ({ children }) => <HeadingRenderer level={2}>{children}</HeadingRenderer>,
              h3: ({ children }) => <HeadingRenderer level={3}>{children}</HeadingRenderer>,
            }}
          >
            {article.body}
          </ReactMarkdown>
        </div>

        <div className="mt-16 pt-8 border-t border-border/60 max-w-2xl">
          <div className="mono uppercase tracking-widest text-[10.5px] text-ink-mute mb-3">
            Enjoyed this piece?
          </div>
          <p className="text-[15px] text-ink-soft leading-relaxed max-w-xl">
            Follow along on{' '}
            <a href={profile.socials.linkedin} target="_blank" rel="noreferrer" className="text-brand hover:underline">
              LinkedIn
            </a>{' '}
            for new posts, or{' '}
            <a href={profile.socials.email} className="text-brand hover:underline">
              reply by email
            </a>{' '}
            - alternate views are welcome.
          </p>
          
          <div className="mt-8 flex items-center gap-3">
            <a
              href={twitterShareUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border text-[13px] text-ink-soft hover:text-foreground hover:bg-brand/10 hover:border-brand/60 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <Share2 size={14} />
              Share on X
            </a>
            <a
              href={linkedinShareUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border text-[13px] text-ink-soft hover:text-foreground hover:bg-brand/10 hover:border-brand/60 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <Linkedin size={14} />
              Share on LinkedIn
            </a>
          </div>
          
          <Link
            to="/writing"
            className="mt-8 inline-flex items-center gap-2 text-[12.5px] mono uppercase tracking-widest text-ink-mute hover:text-brand transition-colors"
          >
            <ArrowLeft size={14} /> All articles
          </Link>
        </div>
      </article>

      {/* Table of Contents Sidebar */}
      <TableOfContents headings={headings} activeId={activeId} />
    </div>
  );
};

export default Article;
